import { useEffect } from 'react';
import { useOrderDetail } from '../../hooks/useOrderDetail';
import { usePickers } from '../../hooks/usePickers';
import { useDrivers } from '../../hooks/useDrivers';
import {
    ORDER_STATUS,
    STATUS_ICONS,
    STATUS_ICONS_DEFAULT,
    STATUS_DESCRIPTIONS,
} from '../../constants/orderStatuses';

interface StatusesLogTabProps {
    orderId: string;
}

type StatusLogEntry = {
    status: { name: string } | null;
};

function getStatusDescription(statusName: string, index: number, allLogs: StatusLogEntry[]): string {
    const before = allLogs.slice(0, index);
    if (statusName === ORDER_STATUS.ASSIGN_DRIVER && before.some(l => l.status?.name === ORDER_STATUS.DRIVER_ASSIGNED)) {
        return 'Driver assignment was revoked. Order has been returned to the dispatch pool for re-assignment.';
    }
    if (statusName === ORDER_STATUS.PACKER_ASSIGNED && before.some(l => l.status?.name === ORDER_STATUS.PACKER_ASSIGNED)) {
        return 'The previous packer was unassigned. A new warehouse member has been assigned to prepare this order.';
    }
    return STATUS_DESCRIPTIONS[statusName] ?? 'Status update recorded.';
}

export const StatusesLogTab = ({ orderId }: StatusesLogTabProps) => {
    const { orderStatuses, loadingStatuses, subscribeToOrderStatus, unsubscribeFromOrderStatus } = useOrderDetail();
    const { pickers, fetchPickers } = usePickers();
    const { drivers, fetchDrivers } = useDrivers();

    useEffect(() => {
        if (pickers.length === 0) void fetchPickers();
        if (drivers.length === 0) void fetchDrivers();
        subscribeToOrderStatus(orderId);
        return () => { unsubscribeFromOrderStatus(); };
    }, [
        orderId,
        pickers.length,
        drivers.length,
        fetchPickers,
        fetchDrivers,
        subscribeToOrderStatus,
        unsubscribeFromOrderStatus,
    ]);

    const getActorName = (modelId: string | null | undefined, modelType: string | null | undefined) => {
        if (!modelId || !modelType) return null;
        if (modelType === 'Picker') {
            const p = pickers.find(p => String(p.id) === String(modelId));
            return p ? `${p.first_name} ${p.last_name}` : 'Unknown Picker';
        }
        if (modelType === 'Driver') {
            const d = drivers.find(d => String(d.auth_id) === String(modelId));
            return d ? d.driver_name : 'Unknown Driver';
        }
        return null;
    };

    if (loadingStatuses) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-stone-200 border-t-[#ee2b74] rounded-full animate-spin" />
                <p className="mt-4 text-sm text-stone-600">Loading order status history…</p>
            </div>
        );
    }

    if (!orderStatuses || orderStatuses.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <p className="text-stone-500">No status updates yet</p>
            </div>
        );
    }

    return (
        <div className="relative">
            <div className="space-y-0">
                {orderStatuses.map((entry, index) => {
                    const statusName        = entry.status?.name ?? '';
                    const colors            = STATUS_ICONS[statusName] ?? STATUS_ICONS_DEFAULT;
                    const isLast            = index === orderStatuses.length - 1;
                    const isFirst           = index === 0;
                    const before            = orderStatuses.slice(0, index);
                    const actorName         = getActorName(entry.model_id, entry.model_type);
                    const isDriver          = entry.model_type === 'Driver';
                    const isDriverRevoked   = statusName === ORDER_STATUS.ASSIGN_DRIVER   && before.some(l => l.status?.name === ORDER_STATUS.DRIVER_ASSIGNED);
                    const isPackerReassigned = statusName === ORDER_STATUS.PACKER_ASSIGNED && before.some(l => l.status?.name === ORDER_STATUS.PACKER_ASSIGNED);

                    return (
                        <div key={entry.id} className="flex gap-6">
                            <div className="flex flex-col items-center">
                                <div className={`relative z-10 w-12 h-12 rounded-full ring-4 ${colors.ring} ${colors.dot} flex items-center justify-center text-white font-semibold text-lg shadow-lg transition-transform hover:scale-110`}>
                                    {colors.icon}
                                </div>
                                {!isLast && <div className={`w-1 h-16 ${colors.dot} mt-2 opacity-30`} />}
                            </div>

                            <div className={`flex-1 ${!isLast ? 'pb-8' : 'pb-0'}`}>
                                <div className={`rounded-lg border-2 ${colors.ring} ${colors.bg} p-4 transition-all hover:shadow-md`}>
                                    <div className="flex items-start justify-between gap-4 mb-3">
                                        <div className="flex-1">
                                            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-tight flex items-center gap-2">
                                                {statusName.replace(/_/g, ' ') || 'UNKNOWN'}
                                                {isDriverRevoked && (
                                                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] rounded-md border border-orange-200">REVOKED</span>
                                                )}
                                                {isPackerReassigned && (
                                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-md border border-amber-200">RE-ASSIGNED</span>
                                                )}
                                            </h4>
                                            {actorName && (
                                                <div className="mt-1.5 flex items-center gap-1.5">
                                                    <span className="text-[10px] font-bold uppercase text-stone-400">{isDriver ? 'Driver' : 'Picker'}:</span>
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ring-1 ring-inset ${isDriver ? 'bg-sky-100 text-sky-700 ring-sky-700/10' : 'bg-pink-100 text-pink-700 ring-pink-700/10'}`}>
                                                        {actorName}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="flex gap-2 mt-2">
                                                {isFirst && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">Started</span>}
                                                {isLast  && <span className="px-2 py-1 bg-[#f0f7e6] text-[#5a7f28] text-xs font-semibold rounded-full">Current State</span>}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs text-stone-600 font-medium">
                                                {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </div>
                                            <div className="text-xs text-stone-500 mt-1">
                                                {new Date(entry.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        {getStatusDescription(statusName, index, orderStatuses)}
                                    </p>
                                    <div className="mt-3 pt-3 border-t border-gray-200 opacity-60">
                                        <div className="text-xs text-stone-600">Step {index + 1} of {orderStatuses.length}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-8 pt-6 border-t-2 border-stone-200">
                <div className="grid grid-cols-3 gap-4">
                    <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                        <p className="text-xs text-blue-600 font-medium uppercase tracking-wide mb-1">Total Updates</p>
                        <p className="text-2xl font-bold text-blue-900">{orderStatuses.length}</p>
                    </div>
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                        <p className="text-xs text-amber-600 font-medium uppercase tracking-wide mb-1">Started</p>
                        <p className="text-sm text-amber-900 font-medium">
                            {new Date(orderStatuses[0]?.created_at).toLocaleDateString()}
                        </p>
                    </div>
                    <div className="rounded-lg bg-[#f0f7e6] border border-[#c5e08a] p-4">
                        <p className="text-xs text-[#5a7f28] font-medium uppercase tracking-wide mb-1">Last Updated</p>
                        <p className="text-sm text-[#5a7f28] font-medium">
                            {new Date(orderStatuses[orderStatuses.length - 1]?.created_at).toLocaleDateString()}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatusesLogTab;